/**
 * QuestionCard Component
 *
 * Phase 8.1: Individual question card with rubric selector
 * Shows question text, observation guidance, and 6-point rubric buttons
 */

import { useState } from 'react';
import { ChevronDown, ChevronUp, Eye, Video, MessageSquare } from 'lucide-react';
import { RubricSelector, RubricLabel } from './RubricSelector';
import type { RubricOption, SectionQuestion } from '@guild/shared';

interface QuestionCardProps {
  question: SectionQuestion;
  rubricOptions: RubricOption[];
  score: number | null;
  feedback: string;
  onScoreChange: (score: number) => void;
  onFeedbackChange: (feedback: string) => void;
  showFeedback?: boolean;
}

export function QuestionCard({
  question,
  rubricOptions,
  score,
  feedback,
  onScoreChange,
  onFeedbackChange,
  showFeedback = true,
}: QuestionCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);

  const hasGuidance = question.observationGuidance || question.videoDetectionCriteria;

  return (
    <div
      className={`
        border rounded-xl transition-all duration-200
        ${score !== null ? 'border-[#00674a]/30 bg-[#00674a]/[0.02]' : 'border-gray-200 bg-white'}
      `}
    >
      {/* Main content */}
      <div className="p-5">
        {/* Question number and required indicator */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
            Q{question.questionNumber}
          </span>
          {question.isRequired && (
            <span className="text-xs text-red-500">*</span>
          )}
          {score !== null && (
            <RubricLabel value={score} options={rubricOptions} />
          )}
        </div>

        {/* Question text */}
        <p className="text-gray-900 font-medium mb-4 leading-relaxed">
          {question.questionText}
        </p>

        {/* Rubric selector */}
        <div className="mb-4">
          <RubricSelector
            value={score}
            onChange={onScoreChange}
            options={rubricOptions}
            size="md"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          {hasGuidance && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#00674a] transition-colors"
            >
              {isExpanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
              {isExpanded ? 'Hide' : 'Show'} guidance
            </button>
          )}

          {showFeedback && (
            <button
              type="button"
              onClick={() => setShowFeedbackInput(!showFeedbackInput)}
              className={`
                flex items-center gap-1.5 text-sm transition-colors
                ${feedback ? 'text-[#00674a] font-medium' : 'text-gray-500 hover:text-[#00674a]'}
              `}
            >
              <MessageSquare className="w-4 h-4" />
              {feedback ? 'Edit note' : 'Add note'}
            </button>
          )}
        </div>
      </div>

      {/* Expandable guidance section */}
      {isExpanded && hasGuidance && (
        <div className="px-5 pb-5 pt-2 border-t border-gray-100 space-y-3">
          {question.observationGuidance && (
            <div className="flex gap-3">
              <Eye className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">
                  Observation Guidance
                </p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {question.observationGuidance}
                </p>
              </div>
            </div>
          )}

          {question.videoDetectionCriteria && (
            <div className="flex gap-3">
              <Video className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-purple-600 uppercase tracking-wider mb-1">
                  Video Detection Criteria
                </p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {question.videoDetectionCriteria}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Feedback input */}
      {showFeedbackInput && (
        <div className="px-5 pb-5 pt-2 border-t border-gray-100">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Feedback note (optional)
          </label>
          <textarea
            value={feedback}
            onChange={(e) => onFeedbackChange(e.target.value)}
            placeholder="Add any observations or feedback for this question..."
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] bg-white resize-none"
          />
        </div>
      )}
    </div>
  );
}

export default QuestionCard;
